import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FaUser,
  FaLock,
  FaArrowRight,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData);

    if (result.success) {
      navigate("/dashboard");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden flex flex-col">
      {/* Background effects */}
      <div className="absolute inset-0 futuristic-grid opacity-20"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-b from-cyan-500/25 via-violet-500/15 to-transparent blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/10 blur-3xl rounded-full"></div>
      <div className="absolute bottom-20 right-0 w-48 h-48 bg-cyan-500/10 blur-3xl rounded-full"></div>

      {/* Top branding area */}
      <div className="relative z-10 flex flex-col items-center pt-16 pb-8 sm:pt-20 sm:pb-10">
        <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-[0_0_40px_rgba(34,211,238,0.4)] mb-5">
          <span className="text-white font-bold text-2xl sm:text-3xl">L</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
          LOOPS
        </h1>
        <p className="text-sm text-cyan-200/60 mt-1 font-medium tracking-wide">
          Digital Chit Fund
        </p>
      </div>

      {/* Form area */}
      <div className="relative z-10 flex-1 flex flex-col px-6 sm:px-8 max-w-md w-full mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white">Welcome back</h2>
          <p className="text-slate-400 text-sm mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          {/* Username field */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Username
            </label>
            <div
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 ${
                focusedField === "username" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <FaUser
                className={`text-sm transition-colors ${
                  focusedField === "username" ? "text-cyan-400" : (
                    "text-slate-500"
                  )
                }`}
              />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onFocus={() => setFocusedField("username")}
                onBlur={() => setFocusedField(null)}
                className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                placeholder="Enter your username"
                autoComplete="off"
                required
              />
            </div>
          </div>

          {/* Password field */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Password
            </label>
            <div
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 ${
                focusedField === "password" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <FaLock
                className={`text-sm transition-colors ${
                  focusedField === "password" ? "text-cyan-400" : (
                    "text-slate-500"
                  )
                }`}
              />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                placeholder="Enter your password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-slate-500 hover:text-slate-300 transition-colors touch-manipulation p-1"
                tabIndex={-1}
              >
                {showPassword ?
                  <FaEyeSlash className="text-sm" />
                : <FaEye className="text-sm" />}
              </button>
            </div>
          </div>

          {/* Forgot password link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-xs text-slate-400 hover:text-cyan-400 transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-cyan-500 via-sky-500 to-violet-600 text-white font-semibold rounded-xl shadow-[0_0_30px_rgba(56,189,248,0.35)] hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation text-sm"
          >
            {loading ?
              <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            : <>
                Sign In
                <FaArrowRight className="text-xs" />
              </>
            }
          </button>
        </form>

        {/* Bottom link */}
        <div className="mt-auto py-8 text-center">
          <p className="text-sm text-slate-400">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
